import { marked } from 'marked';

(function () {
    globalThis.cairos = globalThis.cairos || {};

    const featuresElement = document.getElementById('cairos-features-data');
    const sourcesElement = document.getElementById('cairos-sources-data');
    const uiTextsElement = document.getElementById('cairos-ui-texts-data');
    const analyticsElement = document.getElementById('cairos-analytics-data');
    const form = document.getElementById('chat-form');
    const input = document.getElementById('chat-input');
    const promptChars = document.querySelector('.prompt-chars');
    const currentCharsLength = document.querySelector('.current-chars-length');
    const submitButton = form ? form.querySelector('.chat-submit-button') : null;
    const messages = document.getElementById('chat-messages');
    const wrapper = document.getElementById('chat-wrapper');
    const scrollBtn = document.getElementById('scroll-bottom');
    const initialSuggestionsContainer = document.getElementById('chat-initial-suggestions');
    const assistantArticlesTemplate = document.getElementById('assistant-articles-template');
    const assistantArticleCardTemplate = document.getElementById('assistant-article-card-template');
    const articleImagePlaceholderLightTemplate = document.getElementById('article-image-placeholder-light-template');
    const articleImagePlaceholderDarkTemplate = document.getElementById('article-image-placeholder-dark-template');
    const promptSuggestionsTemplate = document.getElementById('prompt-suggestions-template');
    const promptSuggestionItemTemplate = document.getElementById('prompt-suggestion-item-template');
    let activated = false;
    let suggestionSetCounter = 0;
    let previousResponseId = null;
    let isStreaming = false;

    function normalizeUiTextMap(node) {
        if (!node || typeof node !== 'object' || Array.isArray(node)) {
            return {};
        }

        return Object.entries(node).reduce(function (accumulator, entry) {
            const [rawKey, rawValue] = entry;
            const key = String(rawKey || '').trim();
            if (!key) {
                return accumulator;
            }

            if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
                return accumulator;
            }

            accumulator[key] = String(rawValue ?? '');
            return accumulator;
        }, {});
    }

    function mergeUiTextMaps(baseMap, overrideMap) {
        const normalizedBase = normalizeUiTextMap(baseMap);
        const normalizedOverrides = normalizeUiTextMap(overrideMap);

        return {
            ...normalizedBase,
            ...normalizedOverrides,
        };
    }

    function prepareDefaultUiTexts() {
        if (!uiTextsElement) {
            return {};
        }

        try {
            const uiTextsPayload = JSON.parse(uiTextsElement.textContent || '{}');
            return normalizeUiTextMap(uiTextsPayload);
        } catch {
            return {};
        }
    }

    function getUiText(key, fallback) {
        const uiTexts = globalThis.cairos.uiTexts || {};
        const textKey = String(key || '').trim();
        if (!textKey) {
            return fallback;
        }

        const value = uiTexts[textKey];
        return typeof value === 'string' ? value : fallback;
    }

    function setPromptHintText() {
        const promptHint = document.querySelector('.prompt-hint');
        if (!(promptHint instanceof HTMLElement)) {
            return;
        }

        promptHint.innerHTML = getUiText('prompt_hint_html', '');
    }

    function applyUiTextsToDom() {
        const headingTitle = document.querySelector('.chat-heading h2');
        if (headingTitle) {
            headingTitle.textContent = getUiText('heading_title', headingTitle.textContent || '');
        }

        const headingSubtitle = document.querySelector('.chat-heading p');
        if (headingSubtitle) {
            headingSubtitle.textContent = getUiText('heading_subtitle', headingSubtitle.textContent || '');
        }

        if (input) {
            input.placeholder = getUiText('input_placeholder', '');
            input.setAttribute('aria-label', getUiText('input_aria_label', ''));
        }

        if (scrollBtn) {
            scrollBtn.setAttribute('aria-label', getUiText('scroll_latest_aria_label', ''));
            const label = scrollBtn.querySelector('span');
            if (label) {
                label.textContent = getUiText('scroll_latest_label', '');
            }
        }

        if (promptSuggestionsTemplate instanceof HTMLTemplateElement) {
            const suggestionLabel = promptSuggestionsTemplate.content.querySelector('.chat-rich-text');
            if (suggestionLabel) {
                suggestionLabel.textContent = getUiText('suggestions_intro', '');
            }
        }

        setPromptHintText();
    }

    function getResolvedUiTextsForSource(source) {
        const sourceUiTexts = source && typeof source.ui_texts === 'object' ? source.ui_texts : {};
        return mergeUiTextMaps(globalThis.cairos.defaultUiTexts, sourceUiTexts);
    }

    function normalizeSuggestionItem(rawItem, source, origin) {
        const suggestionOrigin = String(origin || 'api');

        if (typeof rawItem === 'string') {
            const text = rawItem.trim();
            return text ? { text: text, origin: suggestionOrigin } : null;
        }

        if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) {
            return null;
        }

        const text = String(rawItem.text || '').trim();
        if (!text) {
            return null;
        }

        const normalizedItem = { text: text, origin: suggestionOrigin };
        const url = String(rawItem.url || '').trim();
        if (url) {
            normalizedItem.url = toAbsoluteHref(url, source);
        }

        if (typeof rawItem.class === 'string' && rawItem.class) {
            normalizedItem.className = rawItem.class;
        }

        return normalizedItem;
    }

    function normalizeSuggestionItems(items, source, origin) {
        if (!Array.isArray(items)) {
            return [];
        }

        return items
            .map(function (item) {
                return normalizeSuggestionItem(item, source, origin);
            })
            .filter(Boolean);
    }

    function getSourceSuggestionItems(source, sourceKey, origin) {
        const sourceItems = source && Array.isArray(source[sourceKey])
            ? source[sourceKey]
            : [];
        return normalizeSuggestionItems(sourceItems, source, origin);
    }

    function mergeSuggestionItems(prompts, source) {
        const mergedItems = [];
        const seenItems = new Set();

        normalizeSuggestionItems(prompts, source, 'api')
            .concat(getSourceSuggestionItems(source, 'additional_suggestion_items', 'additional'))
            .forEach(function (item) {
                const itemKey = `${item.text}\u001d${item.url || ''}`;
                if (seenItems.has(itemKey)) {
                    return;
                }

                seenItems.add(itemKey);
                mergedItems.push(item);
            });

        return mergedItems;
    }

    function getSuggestionTrackingView() {
        return String(globalThis.location?.pathname || '').trim() || '';
    }

    function emitSuggestionClickTrackingEvent(payload) {
        const sourceSlug = String(globalThis.cairos?.activeSource?.slug || '').trim();
        const detail = {
            text: String(payload?.text || '').trim(),
            url: String(payload?.url || '').trim(),
            action: String(payload?.action || '').trim(),
            origin: String(payload?.origin || '').trim(),
            isCta: Boolean(payload?.isCta),
            sourceSlug: sourceSlug,
            view: getSuggestionTrackingView(),
            timestamp: new Date().toISOString(),
        };

        globalThis.cairos.lastSuggestionClickEvent = detail;

        globalThis.dispatchEvent(new CustomEvent('cairos:suggestion-click', {
            detail: detail,
        }));
    }

    function prepareFeatures() {
        const features = {
            isLoggingEnabled: false,
            logCreateEndpoint: '/logging/create',
        };

        if (featuresElement) {
            try {
                const featuresPayload = JSON.parse(featuresElement.textContent || '{}');

                features.isLoggingEnabled = Boolean(featuresPayload.isLoggingEnabled);
                features.logCreateEndpoint = String(featuresPayload.logCreateEndpoint || '/logging/create').trim();
            } catch {
                console.error('[cAIros] Failed to parse features payload. Using default feature configuration.');
            }
        }

        return features;
    }

    function prepareAnalytics() {
        const analytics = {
            isEnabled: false,
            utmSource: '',
            utmMedium: '',
            utmCampaign: '',
            utmContentTemplate: '',
        };

        if (!analyticsElement) {
            return analytics;
        }

        try {
            const analyticsPayload = JSON.parse(analyticsElement.textContent || '{}');

            analytics.isEnabled = analyticsPayload.enabled === true;
            analytics.utmSource = String(analyticsPayload.utm_source || '').trim();
            analytics.utmMedium = String(analyticsPayload.utm_medium || '').trim();
            analytics.utmCampaign = String(analyticsPayload.utm_campaign || '').trim();
            analytics.utmContentTemplate = String(analyticsPayload.utm_content_template || '').trim();
        } catch {
            console.error('[cAIros] Failed to parse analytics payload. Using default analytics configuration.');
        }

        return analytics;
    }

    globalThis.cairos.features = prepareFeatures();
    globalThis.cairos.isLoggingEnabled = globalThis.cairos.features.isLoggingEnabled;
    globalThis.cairos.analytics = prepareAnalytics();
    globalThis.cairos.defaultUiTexts = prepareDefaultUiTexts();
    globalThis.cairos.uiTexts = globalThis.cairos.defaultUiTexts;

    async function createChatLog(prompt, responsePayload, toolCalls, source) {
        if (!globalThis.cairos.isLoggingEnabled) {
            return;
        }

        const identifier = String(source?.auth?.identifier || '').trim();
        const token = String(source?.auth?.token || '').trim();
        const sourceSlug = String(source?.slug || '').trim();
        if (!identifier || !token || !sourceSlug) {
            console.error('[cAIros] Chat logging skipped: missing identifier, token, or source slug.');
            return;
        }

        const response = await fetch(globalThis.cairos.features.logCreateEndpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                identifier: identifier,
                token: token,
                source_slug: sourceSlug,
                prompt: prompt,
                response: responsePayload,
                tool_calls: Array.isArray(toolCalls) ? toolCalls : [],
            }),
        });

        if (!response.ok) {
            throw new Error(`Chat logging failed with status ${response.status}`);
        }
    }

    function queueChatLog(prompt, responsePayload, toolCalls, source) {
        if (!globalThis.cairos.isLoggingEnabled) {
            return;
        }

        void createChatLog(prompt, responsePayload, toolCalls, source).catch(function (error) {
            console.error('[cAIros] Chat logging failed:', error);
        });
    }

    function emitChatStreamCompleteEvent(prompt, responsePayload, source, promptContext) {
        const detail = {
            prompt: prompt,
            response: responsePayload || null,
            sourceSlug: source?.slug,
            promptSource: promptContext.type,
            suggestionOrigin: promptContext.suggestionOrigin,
            view: getSuggestionTrackingView(),
            timestamp: new Date().toISOString(),
        };

        globalThis.cairos.lastChatStreamCompleteEvent = detail;


        globalThis.dispatchEvent(new CustomEvent('cairos:chat-stream-complete', {
            detail: detail,
        }));
    }

    function mapSourcesList(sourcesPayload) {
        if (!Array.isArray(sourcesPayload)) {
            return {};
        }

        return sourcesPayload.reduce(function (sourcesBySlug, source) {
            const slug = String(source?.slug || '').trim();
            if (!slug) {
                return sourcesBySlug;
            }

            sourcesBySlug[slug] = source;
            return sourcesBySlug;
        }, {});
    }

    function prepareSources() {
        if (!sourcesElement) {
            return {};
        }

        try {
            const sourcesPayload = JSON.parse(sourcesElement.textContent || '[]');
            return mapSourcesList(sourcesPayload);
        } catch {
            return {};
        }
    }

    globalThis.cairos.sources = prepareSources();

    function setSources(sourcesPayload, activeSourceSlug) {
        globalThis.cairos.sources = mapSourcesList(sourcesPayload);

        if (activeSourceSlug) setActiveSource(activeSourceSlug);

        return globalThis.cairos.sources;
    }

    function setActiveSource(slug) {
        const sourceSlug = String(slug || '').trim();
        if (!sourceSlug) {
            globalThis.cairos.activeSource = null;
            globalThis.cairos.uiTexts = globalThis.cairos.defaultUiTexts;
            applyUiTextsToDom();
            clearInitialSuggestions();
            return null;
        }

        const source = globalThis.cairos.sources[sourceSlug] || null;

        globalThis.cairos.activeSource = source;
        globalThis.cairos.uiTexts = getResolvedUiTextsForSource(source);
        applyUiTextsToDom();
        renderInitialSuggestions(source);

        return source;
    }

    globalThis.cairos.setActiveSource = setActiveSource;
    globalThis.cairos.setSources = setSources;

    const availableSourceSlugs = Object.keys(globalThis.cairos.sources);
    if (availableSourceSlugs.length) {
        setActiveSource(availableSourceSlugs[0]);
    } else {
        applyUiTextsToDom();
    }

    if (!form || !input || !messages || !wrapper) {
        return;
    }

    const promptCharsShakeDuration = 320;

    function updateCurrentCharsLength() {
        if (!currentCharsLength) {
            return;
        }

        const currentLength = input.value.length;
        const maxLength = Number.parseInt(input.maxLength, 10);

        currentCharsLength.textContent = currentLength;

        if (Number.isFinite(maxLength) && maxLength > 0) {
            const remainingChars = maxLength - currentLength;
            currentCharsLength.classList.toggle('is-warning-limit', remainingChars <= 100 && remainingChars > 50);
            currentCharsLength.classList.toggle('is-near-limit', remainingChars <= 50 && remainingChars > 0);
            currentCharsLength.classList.toggle('is-at-limit', remainingChars <= 0);
        }

        if (promptChars && input.value.length > 0) {
            promptChars.classList.remove('invisible');
        }
    }

    function triggerPromptCharsLimitShake() {
        if (!promptChars) {
            return;
        }

        promptChars.classList.remove('is-limit-shake');
        void promptChars.offsetWidth;
        promptChars.classList.add('is-limit-shake');

        globalThis.setTimeout(function () {
            promptChars.classList.remove('is-limit-shake');
        }, promptCharsShakeDuration);
    }

    function shouldShakePromptChars(event) {
        if (!event || event.defaultPrevented || isStreaming) {
            return false;
        }

        const maxLength = Number.parseInt(input.maxLength, 10);
        if (!Number.isFinite(maxLength) || maxLength <= 0) {
            return false;
        }

        const inputType = String(event.inputType || '');
        if (!inputType.startsWith('insert')) {
            return false;
        }

        const selectionStart = input.selectionStart ?? input.value.length;
        const selectionEnd = input.selectionEnd ?? input.value.length;
        const hasSelection = selectionEnd > selectionStart;

        if (hasSelection) {
            return false;
        }

        return input.value.length >= maxLength;
    }

    updateCurrentCharsLength();

    function isAtBottom() {
        return messages.scrollHeight - messages.scrollTop - messages.clientHeight < 10;
    }

    function smoothScrollToBottom(duration) {
        const start = messages.scrollTop;
        const end = messages.scrollHeight - messages.clientHeight;
        const distance = end - start;
        if (distance <= 0) {
            return;
        }

        let startTime = null;

        function easeInOutCubic(t) {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        }

        function step(timestamp) {
            if (!startTime) {
                startTime = timestamp;
            }

            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            messages.scrollTop = start + distance * easeInOutCubic(progress);

            if (progress < 1) {
                requestAnimationFrame(step);
            }
        }

        requestAnimationFrame(step);
    }

    if (scrollBtn) {
        messages.addEventListener('scroll', function () {
            if (!activated) {
                return;
            }

            scrollBtn.hidden = isAtBottom();
        });

        scrollBtn.addEventListener('click', function () {
            smoothScrollToBottom(340);
            scrollBtn.hidden = true;
        });
    }

    function activate() {
        if (activated) {
            return;
        }

        activated = true;
        clearInitialSuggestions();
        wrapper.classList.add('is-active');
        messages.classList.remove('hidden');
        messages.classList.add('is-active');
        input.rows = 1;
    }

    function getSourceBaseUrl(source) {
        const sourceObject = source || globalThis.cairos.activeSource || null;
        const configuredBaseUrl = String(sourceObject?.url || '').trim();
        if (configuredBaseUrl) {
            return configuredBaseUrl;
        }

        const apiUrl = String(sourceObject?.api_url || '').trim();
        if (!apiUrl) {
            return '';
        }

        try {
            return new URL(apiUrl).origin;
        } catch {
            return '';
        }
    }

    function toAbsoluteHref(href, source) {
        const rawHref = String(href || '').trim();
        if (!rawHref) {
            return rawHref;
        }

        const baseUrl = getSourceBaseUrl(source);

        if (/^[a-z][a-z0-9+.-]*:/i.test(rawHref) || rawHref.startsWith('//')) {
            return rawHref;
        }

        if (rawHref.startsWith('#')) {
            return rawHref;
        }

        if (!baseUrl) {
            return rawHref;
        }

        try {
            return new URL(rawHref, baseUrl).toString();
        } catch {
            return rawHref;
        }
    }

    function normalizeMessageLinks(container, source) {
        const links = container.querySelectorAll('a[href]');
        links.forEach(function (link) {
            const rawHref = link.getAttribute('href');
            if (rawHref) {
                const normalizedHref = toAbsoluteHref(rawHref, source);
                link.href = normalizedHref;

                const currentText = String(link.textContent || '');
                if (currentText && currentText.trim() === rawHref.trim()) {
                    link.textContent = normalizedHref;
                }

                const title = link.getAttribute('title');
                if (title && title.trim() === rawHref.trim()) {
                    link.setAttribute('title', normalizedHref);
                }
            }
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
        });
    }

    function cloneTemplate(template) {
        if (!(template instanceof HTMLTemplateElement)) {
            return null;
        }

        const element = template.content.firstElementChild;
        if (!element) {
            return null;
        }

        return element.cloneNode(true);
    }

    function getSvgDataUriFromTemplate(templateElement) {
        if (!(templateElement instanceof HTMLTemplateElement)) {
            return '';
        }

        const svgMarkup = templateElement.innerHTML.trim();
        if (!svgMarkup) {
            return '';
        }

        return `data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`;
    }

    function getArticleImagePlaceholder() {
        const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
        const activeTemplate = isDarkTheme
            ? articleImagePlaceholderDarkTemplate
            : articleImagePlaceholderLightTemplate;
        const fallbackTemplate = isDarkTheme
            ? articleImagePlaceholderLightTemplate
            : articleImagePlaceholderDarkTemplate;

        return getSvgDataUriFromTemplate(activeTemplate) || getSvgDataUriFromTemplate(fallbackTemplate);
    }

    function refreshThemeSensitiveUi() {
        const placeholderSrc = getArticleImagePlaceholder();
        if (placeholderSrc && messages) {
            messages.querySelectorAll('img[data-placeholder-image="true"]').forEach(function (imageElement) {
                imageElement.src = placeholderSrc;
            });
        }
    }

    function clearInitialSuggestions() {
        if (!(initialSuggestionsContainer instanceof HTMLElement)) {
            return;
        }

        initialSuggestionsContainer.innerHTML = '';
        initialSuggestionsContainer.hidden = true;
    }

    function renderInitialSuggestions(source) {
        if (!(initialSuggestionsContainer instanceof HTMLElement)) {
            return;
        }

        const sourceObject = source || globalThis.cairos.activeSource || null;
        const suggestionItems = getSourceSuggestionItems(
            sourceObject,
            'initial_suggestion_items',
            'initial'
        );
        const introText = getUiText('initial_suggestions_intro', getUiText('suggestions_intro', ''));

        initialSuggestionsContainer.innerHTML = '';

        const node = buildSuggestionNode(suggestionItems, {
            keepAll: true,
            introText: introText,
        });

        if (!node) {
            initialSuggestionsContainer.hidden = true;
            return;
        }

        initialSuggestionsContainer.appendChild(node);
        initialSuggestionsContainer.hidden = false;
    }

    function setCarouselFadeSide(gridElement, side) {
        if (!(gridElement instanceof HTMLElement)) {
            return;
        }

        const normalizedSide = side === 'left' || side === 'right' ? side : 'none';
        gridElement.dataset.carouselFadeSide = normalizedSide;
    }

    function updateCarouselFadeSide(gridElement, state, directionHint) {
        if (!(gridElement instanceof HTMLElement)) {
            return;
        }

        const maxScrollLeft = Math.max(0, gridElement.scrollWidth - gridElement.clientWidth);
        if (maxScrollLeft < 1) {
            state.lastSide = 'none';
            setCarouselFadeSide(gridElement, 'none');
            return;
        }

        const tolerance = 2;
        const currentScrollLeft = gridElement.scrollLeft;
        const atStart = currentScrollLeft <= tolerance;
        const atEnd = currentScrollLeft >= maxScrollLeft - tolerance;

        if (atStart && !atEnd) {
            state.lastSide = 'right';
            setCarouselFadeSide(gridElement, state.lastSide);
            return;
        }

        if (atEnd && !atStart) {
            state.lastSide = 'left';
            setCarouselFadeSide(gridElement, state.lastSide);
            return;
        }

        if (directionHint > 0) {
            state.lastSide = 'right';
        } else if (directionHint < 0) {
            state.lastSide = 'left';
        } else if (state.lastSide !== 'left' && state.lastSide !== 'right') {
            state.lastSide = 'right';
        }

        setCarouselFadeSide(gridElement, state.lastSide);
    }

    function setupCarouselFade(gridElement) {
        if (!(gridElement instanceof HTMLElement)) {
            return;
        }

        if (gridElement.dataset.carouselFadeInitialized === 'true') {
            return;
        }

        gridElement.dataset.carouselFadeInitialized = 'true';

        const state = {
            lastScrollLeft: gridElement.scrollLeft,
            lastSide: 'none',
            pendingDirection: 0,
            lastTouchX: null,
        };

        function refresh(directionHint) {
            updateCarouselFadeSide(gridElement, state, directionHint);
            state.lastScrollLeft = gridElement.scrollLeft;
        }

        function setPendingDirection(direction) {
            if (direction !== 1 && direction !== -1) {
                return;
            }

            state.pendingDirection = direction;
            refresh(direction);
        }

        gridElement.addEventListener('wheel', function (event) {
            const absX = Math.abs(event.deltaX || 0);
            const absY = Math.abs(event.deltaY || 0);
            let dominantDelta = 0;

            if (absX >= absY) {
                dominantDelta = event.deltaX || 0;
            } else if (event.shiftKey && absY > 0.5) {
                dominantDelta = event.deltaY || 0;
            }

            if (Math.abs(dominantDelta) > 0.5) {
                setPendingDirection(dominantDelta > 0 ? 1 : -1);
            }
        }, { passive: true });

        gridElement.addEventListener('touchstart', function (event) {
            if (!event.touches || !event.touches.length) {
                return;
            }

            state.lastTouchX = event.touches[0].clientX;
        }, { passive: true });

        gridElement.addEventListener('touchmove', function (event) {
            if (!event.touches || !event.touches.length || state.lastTouchX === null) {
                return;
            }

            const currentTouchX = event.touches[0].clientX;
            const deltaX = state.lastTouchX - currentTouchX;

            if (Math.abs(deltaX) > 1) {
                setPendingDirection(deltaX > 0 ? 1 : -1);
            }

            state.lastTouchX = currentTouchX;
        }, { passive: true });

        gridElement.addEventListener('touchend', function () {
            state.lastTouchX = null;
        }, { passive: true });

        gridElement.addEventListener('keydown', function (event) {
            if (event.key === 'ArrowRight') {
                setPendingDirection(1);
            } else if (event.key === 'ArrowLeft') {
                setPendingDirection(-1);
            }
        });

        gridElement.addEventListener('scroll', function () {
            const delta = gridElement.scrollLeft - state.lastScrollLeft;
            let directionHint = 0;

            if (Math.abs(delta) > 0.5) {
                directionHint = delta > 0 ? 1 : -1;
                state.pendingDirection = directionHint;
            } else if (state.pendingDirection === 1 || state.pendingDirection === -1) {
                directionHint = state.pendingDirection;
            }

            refresh(directionHint);
        }, { passive: true });

        if (typeof ResizeObserver === 'function') {
            const resizeObserver = new ResizeObserver(function () {
                refresh(0);
            });
            resizeObserver.observe(gridElement);
        }

        refresh(0);
        requestAnimationFrame(function () {
            refresh(0);
        });
    }

    function setupCarouselFades(containerElement) {
        if (!(containerElement instanceof HTMLElement)) {
            return;
        }

        containerElement.querySelectorAll('.chat-articles-grid').forEach(function (gridElement) {
            setupCarouselFade(gridElement);
        });
    }

    function toArticleCardClassToken(cardClassValue) {
        return String(cardClassValue || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    const COPY_FEEDBACK_DURATION_MS = 900;

    function showShareButtonCopyFeedback(shareButton) {
        if (!(shareButton instanceof HTMLElement)) {
            return;
        }

        const activeTimeoutId = Number(shareButton.dataset.copyFeedbackTimeoutId || 0);
        if (activeTimeoutId) {
            clearTimeout(activeTimeoutId);
        }

        shareButton.classList.add('is-copied');

        const timeoutId = globalThis.setTimeout(function () {
            shareButton.classList.remove('is-copied');
            delete shareButton.dataset.copyFeedbackTimeoutId;
        }, COPY_FEEDBACK_DURATION_MS);

        shareButton.dataset.copyFeedbackTimeoutId = String(timeoutId);
    }

    /**
     * Builds a single article card from a normalized article object.
     * Expected shape: { url, title, imageUrl }.
     */
    function buildArticleCard(article, index) {
        const card = cloneTemplate(assistantArticleCardTemplate);
        if (!card) {
            return null;
        }

        let href = String(article.url || '#').trim() || '#';
        const title = String(article.title || getUiText('article_untitled', '')).trim();
        const imageUrl = String(article.imageUrl || '').trim();
        const description = String(
            article.description
            || article.lead_text
            || ''
        ).trim();
        const label = String(article.label || '').trim();
        const cardClass = toArticleCardClassToken(article.card_class);
        const commentCount = article.comment_count !== undefined ? article.comment_count : null;
        const totalReactionCount = article.total_reaction_count !== undefined ? article.total_reaction_count : null;
        const analyticsData = globalThis.cairos.analytics || {};

        if (analyticsData && analyticsData.isEnabled) {
            try {
                const url = new URL(href);
                const originalHref = href;
                let utmContentValue = '';
                const isFromActiveSource = globalThis.cairos.activeSource.url === url.origin;

                if (analyticsData?.utmSource) {
                    url.searchParams.set('utm_source', analyticsData.utmSource);
                }

                if (analyticsData?.utmMedium) {
                    url.searchParams.set('utm_medium', analyticsData.utmMedium);
                }

                if (analyticsData?.utmCampaign) {
                    url.searchParams.set('utm_campaign', analyticsData.utmCampaign);
                }

                if (analyticsData?.utmContentTemplate) {
                    utmContentValue = `${analyticsData.utmContentTemplate}${String(index + 1).padStart(2, '0')}`;
                    url.searchParams.set('utm_content', utmContentValue);
                }

                if (dataLayer && dataLayer.push && isFromActiveSource && utmContentValue) {
                    card.addEventListener('click', function () {
                        dataLayer.push({
                            event: 'Click',
                            eventCategory: '24HEJ',
                            eventAction: utmContentValue,
                            eventLabel: originalHref
                        });
                    });
                }

                href = url.toString();
            } catch { }
        }

        card.href = href;

        const labelElement = card.querySelector('.chat-article-label');
        if (labelElement) {
            Array.from(labelElement.classList).forEach(function (className) {
                if (className.startsWith('is-')) {
                    labelElement.classList.remove(className);
                }
            });

            if (cardClass) {
                labelElement.classList.add(`is-${cardClass}`);
            }

            if (label) {
                labelElement.textContent = label;
                labelElement.hidden = false;
            } else {
                labelElement.hidden = true;
            }
        }

        // Set title and truncate to 3 lines
        const titleElement = card.querySelector('.chat-article-title');
        if (titleElement) {
            titleElement.textContent = title;
        }

        // Set description if available
        const descriptionElement = card.querySelector('.chat-article-description');
        if (descriptionElement) {
            if (description) {
                descriptionElement.textContent = description;
                descriptionElement.hidden = false;
            } else {
                descriptionElement.hidden = true;
            }
        }

        // Handle image
        const imageWrap = card.querySelector('.chat-article-image');
        const imageElement = card.querySelector('.chat-article-image-element');
        if (imageElement && imageWrap) {
            if (imageUrl) {
                delete imageElement.dataset.placeholderImage;
                imageElement.src = imageUrl;
                imageElement.alt = title;
            } else {
                imageElement.dataset.placeholderImage = 'true';
                imageElement.src = getArticleImagePlaceholder();
                imageElement.alt = getUiText('article_image_placeholder_alt', '');
            }
        }

        // Handle comment count
        const commentBubble = card.querySelector('.chat-article-comment');
        const commentCountElement = card.querySelector('.chat-article-comment-count');
        if (commentBubble) {
            if (commentCount !== null && commentCount > 0) {
                if (commentCountElement) {
                    commentCountElement.textContent = commentCount;
                }
                commentBubble.style.display = 'inline-flex';
            } else {
                commentBubble.style.display = 'none';
            }
        }

        // Handle reaction count
        const reactionElement = card.querySelector('.chat-article-reaction');
        const reactionValueElement = card.querySelector('.chat-article-reaction-value');
        if (reactionElement) {
            if (totalReactionCount !== null && totalReactionCount > 0) {
                if (reactionValueElement) {
                    reactionValueElement.textContent = totalReactionCount;
                }
                reactionElement.style.display = 'inline-flex';
            } else {
                reactionElement.style.display = 'none';
            }
        }

        // Handle share button
        const shareBtn = card.querySelector('.chat-article-share-btn');
        if (shareBtn) {
            const shareButtonLabel = String(shareBtn.getAttribute('aria-label') || '').trim();
            if (shareButtonLabel) {
                shareBtn.setAttribute('title', shareButtonLabel);
            }

            shareBtn.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();

                if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
                    console.error('[cAIros] Clipboard API is not available in this browser context.');
                    return;
                }

                navigator.clipboard.writeText(href).then(function () {
                    showShareButtonCopyFeedback(shareBtn);
                }).catch(function (err) {
                    console.error('[cAIros] Failed to copy to clipboard:', err);
                });
            });
        }

        return card;
    }

    function buildAssistantArticlesNode(payload) {
        const text = String(payload.text || '').trim();
        const articles = Array.isArray(payload.articles) ? payload.articles : [];
        const root = cloneTemplate(assistantArticlesTemplate);
        if (!root) {
            return null;
        }

        const textElement = root.querySelector('[data-role="text"]');
        const grid = root.querySelector('[data-role="articles-grid"]');

        if (textElement) {
            if (text) {
                textElement.textContent = text;
            } else {
                textElement.remove();
            }
        }

        if (grid) {
            articles.forEach(function (article, index) {
                const card = buildArticleCard(article, index);
                if (card) {
                    grid.appendChild(card);
                }
            });

            if (!grid.children.length) {
                grid.remove();
            }
        }

        if (!text && !articles.length) {
            return null;
        }

        return root;
    }

    function getVisibleSuggestionItems(suggestionItems, maxApiItems) {
        if (!Array.isArray(suggestionItems)) {
            return [];
        }

        const normalizedMaxApi = Number.isFinite(maxApiItems) && maxApiItems > 0
            ? Math.floor(maxApiItems)
            : 4;

        const visibleApiItems = suggestionItems.filter(function (item) {
            return item && item.origin !== 'additional';
        }).slice(0, normalizedMaxApi);

        const additionalItems = suggestionItems.filter(function (item) {
            return item && item.origin === 'additional';
        });

        return visibleApiItems.concat(additionalItems);
    }

    function buildSuggestionNode(suggestionItems, options) {
        const settings = options || {};
        const list = settings.keepAll
            ? (Array.isArray(suggestionItems) ? suggestionItems.filter(Boolean) : [])
            : getVisibleSuggestionItems(suggestionItems, 4);
        if (!list.length) {
            return null;
        }

        const root = cloneTemplate(promptSuggestionsTemplate);
        if (!root) {
            return null;
        }

        const suggestionList = root.querySelector('[data-role="suggestions-list"]');
        if (!suggestionList) {
            return null;
        }

        const introElement = root.querySelector('.chat-rich-text');
        if (introElement && typeof settings.introText === 'string') {
            introElement.textContent = settings.introText;
        }

        suggestionSetCounter += 1;
        const setId = `set-${suggestionSetCounter}`;

        list.forEach(function (suggestionItem) {
            const item = cloneTemplate(promptSuggestionItemTemplate);
            if (!item) {
                return;
            }

            item.dataset.suggestionSet = setId;
            item.dataset.suggestionText = suggestionItem.text;
            item.dataset.suggestionOrigin = suggestionItem.origin || 'api';
            if (suggestionItem.url) {
                item.dataset.suggestionUrl = suggestionItem.url;
            }
            if (suggestionItem.className) {
                item.dataset.suggestionClass = suggestionItem.className;
                item.className = `${item.className} ${suggestionItem.className}`.trim();
            }
            item.textContent = suggestionItem.text;
            suggestionList.appendChild(item);
        });

        if (!suggestionList.children.length) {
            return null;
        }

        suggestionList.dataset.suggestionSet = setId;
        return root;
    }

    function addMessage(content, role, options) {
        const settings = options || {};
        const element = document.createElement('div');
        element.className = `chat-bubble ${role}`;

        if (role === 'assistant' && !messages.querySelector('.chat-bubble.assistant')) {
            element.classList.add('is-initial-assistant');
        }

        if (settings.node && content instanceof Node) {
            element.appendChild(content);
            normalizeMessageLinks(element);
        } else if (settings.html) {
            element.innerHTML = String(content || '');
            normalizeMessageLinks(element);
        } else {
            const textElement = document.createElement('div');
            textElement.className = 'chat-message-text';
            textElement.textContent = String(content || '');
            element.appendChild(textElement);
        }

        messages.appendChild(element);
        setupCarouselFades(element);

        if (role === 'user' || isAtBottom()) {
            messages.scrollTop = messages.scrollHeight;
        }

        return element;
    }

    function renderAssistantMarkdown(targetElement, markdownSource, options) {
        const settings = options || {};
        if (!(targetElement instanceof HTMLElement)) {
            return;
        }

        const textElement = targetElement.querySelector('.chat-message-text');
        if (!textElement) {
            return;
        }

        const markdownText = String(markdownSource || '');
        if (!markdownText) {
            textElement.textContent = '';
            return;
        }

        try {
            const renderedHtml = marked.parse(markdownText, {
                gfm: true,
                breaks: true,
            });
            textElement.innerHTML = renderedHtml;

            if (settings.showStreamingIndicator) {
                const streamingIndicator = document.createElement('span');
                streamingIndicator.className = 'chat-text-stream-loader';
                streamingIndicator.setAttribute('aria-busy', 'true');
                streamingIndicator.setAttribute('aria-label', getUiText('streaming_response_aria_label', ''));
                textElement.appendChild(streamingIndicator);
            }

            setupCarouselFades(targetElement);
            normalizeMessageLinks(targetElement, settings.source);
        } catch {
            textElement.textContent = markdownText;
        }
    }

    function renderPromptSuggestions(prompts, source) {
        const node = buildSuggestionNode(mergeSuggestionItems(prompts, source), {
            keepAll: false,
            introText: getUiText('suggestions_intro', ''),
        });
        if (!node) {
            return null;
        }

        return addMessage(node, 'assistant', { node: true });
    }

    function handleUserPrompt(text) {
        const userPrompt = String(text || '').trim();
        if (!userPrompt) {
            return null;
        }

        activate();
        const element = addMessage(userPrompt, 'user');
        if (scrollBtn) {
            scrollBtn.hidden = true;
        }
        return element;
    }

    /**
     * TODO: when DEBUG flag will be provided in this script,
     * Set enabled only when DEBUG is true, and available via 
     * globalThis.cairos.simulateAssistantPayload only in DEBUG.
     */
    function simulateAssistantMessage(text) {
        const messageText = String(text || '').trim();
        if (!messageText) {
            return null;
        }

        activate();
        const element = addMessage(messageText, 'assistant');
        if (scrollBtn) {
            scrollBtn.hidden = true;
        }

        return element;
    }

    function simulateAssistantHtmlMessage(html) {
        const htmlText = String(html || '').trim();
        if (!htmlText) {
            return null;
        }

        activate();
        const element = addMessage(htmlText, 'assistant', { html: true });
        if (scrollBtn) {
            scrollBtn.hidden = true;
        }

        return element;
    }

    function simulateAssistantPayload(payload) {
        const node = buildAssistantArticlesNode(payload || {});
        if (!node) {
            return null;
        }

        activate();
        const element = addMessage(node, 'assistant', { node: true });
        if (scrollBtn) {
            scrollBtn.hidden = true;
        }

        return element;
    }

    function simulatePromptSuggestions(prompts) {
        activate();
        const element = renderPromptSuggestions(prompts, globalThis.cairos.activeSource || null);
        if (scrollBtn) {
            scrollBtn.hidden = true;
        }

        refreshThemeSensitiveUi();

        return element;
    }

    function setStreamingState(streaming) {
        isStreaming = streaming;
        input.disabled = streaming;
        input.placeholder = streaming
            ? getUiText('input_placeholder_answering', '')
            : getUiText('input_placeholder', '');
        if (submitButton) {
            submitButton.disabled = streaming;
        }
    }

    function scrollToMessageStart(messageElement, smooth) {
        if (!(messageElement instanceof HTMLElement)) {
            return;
        }

        const bottomClearance = 88;
        const targetTop = Math.max(0, messageElement.offsetTop - (messages.clientHeight - bottomClearance));
        const behavior = smooth ? 'smooth' : 'auto';

        if (typeof messages.scrollTo === 'function') {
            try {
                messages.scrollTo({
                    top: targetTop,
                    behavior: behavior,
                });
                return;
            } catch {
                // Fallback below if scroll options are not supported.
            }
        }

        messages.scrollTop = targetTop;
    }

    function addThinkingLoader() {
        activate();
        const loaderElement = addMessage('', 'assistant');
        loaderElement.dataset.loading = 'thinking';
        loaderElement.classList.add('is-thinking');

        const textElement = loaderElement.querySelector('.chat-message-text');
        if (textElement) {
            const loadingStatus = document.createElement('span');
            loadingStatus.setAttribute('aria-busy', 'true');
            loadingStatus.textContent = getUiText('thinking', '');
            textElement.appendChild(loadingStatus);
        }

        return loaderElement;
    }

    function appendAssistantChunk(targetElement, chunkText, source) {
        const text = String(chunkText || '');
        if (!text) {
            return targetElement;
        }

        let assistantElement = targetElement;
        if (!assistantElement) {
            assistantElement = addMessage('', 'assistant');
            assistantElement.dataset.markdownSource = '';
        }

        const textElement = assistantElement.querySelector('.chat-message-text');
        if (!textElement) {
            return assistantElement;
        }

        if (assistantElement.dataset.loading === 'thinking') {
            delete assistantElement.dataset.loading;
            assistantElement.classList.remove('is-thinking');
            textElement.textContent = '';
            assistantElement.dataset.markdownSource = '';
        }

        const markdownSource = `${assistantElement.dataset.markdownSource || ''}${text}`;
        assistantElement.dataset.markdownSource = markdownSource;
        assistantElement.dataset.textStreaming = 'true';
        renderAssistantMarkdown(assistantElement, markdownSource, {
            showStreamingIndicator: true,
            source: source,
        });
        return assistantElement;
    }

    function finalizeAssistantTextStreaming(targetElement, source) {
        if (!(targetElement instanceof HTMLElement)) {
            return;
        }

        if (targetElement.dataset.textStreaming !== 'true') {
            return;
        }

        let markdownSource = String(targetElement.dataset.markdownSource || '');
        targetElement.dataset.textStreaming = 'false';

        const jsonArrayPattern = /\s*\[\s*"[^[\]]*"(\s*,\s*"[^[\]]*")*\s*\]\s*$/;
        markdownSource = markdownSource.replace(jsonArrayPattern, '').trimEnd();

        renderAssistantMarkdown(targetElement, markdownSource, {
            showStreamingIndicator: false,
            source: source,
        });
    }

    function addRelatedNewsLoader(targetElement) {
        if (!(targetElement instanceof HTMLElement)) {
            return null;
        }

        const existingLoader = targetElement.querySelector('[data-role="related-news-loader"]');
        if (existingLoader) {
            return existingLoader;
        }

        const loaderElement = document.createElement('div');
        loaderElement.className = 'chat-inline-loader';
        loaderElement.dataset.role = 'related-news-loader';

        const loadingStatus = document.createElement('span');
        loadingStatus.setAttribute('aria-busy', 'true');

        loaderElement.appendChild(loadingStatus);
        targetElement.appendChild(loaderElement);

        return loaderElement;
    }

    function removeRelatedNewsLoader(targetElement) {
        if (!(targetElement instanceof HTMLElement)) {
            return;
        }

        const loaderElement = targetElement.querySelector('[data-role="related-news-loader"]');
        if (loaderElement) {
            loaderElement.remove();
        }
    }

    function isNetworkFetchError(error) {
        const message = String(error?.message || '').toLowerCase();
        return error instanceof TypeError
            || message.includes('network')
            || message.includes('failed to fetch')
            || message.includes('fetch');
    }

    function mapToolArticles(dataArray, source) {
        if (!Array.isArray(dataArray)) {
            return [];
        }

        return dataArray
            .map(function (item) {
                if (!item || typeof item !== 'object') {
                    return null;
                }

                const rawLink = String(item.link || '').trim();
                const title = String(item.title || '').trim();
                const imageUrl = String(
                    item.cover_image?.url
                    || item.image?.url
                    || ''
                ).trim();

                if (!rawLink || !title) {
                    return null;
                }

                const absoluteUrl = rawLink.startsWith('http')
                    ? rawLink
                    : toAbsoluteHref(rawLink, source);

                const description = String(
                    item.description
                    || item.lead_text
                    || ''
                ).trim();
                const attributes = item.attributes && typeof item.attributes === 'object'
                    ? item.attributes
                    : {};
                const label = String(item.label || attributes.label || '').trim();
                const cardClass = String(item.card_class || attributes.card_class || '').trim();
                const commentCount = Number.isFinite(item.comment_count)
                    ? item.comment_count
                    : undefined;
                const totalReactionCount = Number.isFinite(item.total_reaction_count)
                    ? item.total_reaction_count
                    : undefined;

                return {
                    url: absoluteUrl,
                    title: title,
                    imageUrl: imageUrl,
                    description: description,
                    label: label,
                    card_class: cardClass,
                    comment_count: commentCount,
                    total_reaction_count: totalReactionCount,
                };
            })
            .filter(Boolean);
    }

    function getChatRequestUrl(apiUrl) {
        const baseUrl = String(apiUrl || '').trim().replace(/\/+$/, '');
        if (!baseUrl) {
            return '';
        }

        return `${baseUrl}/chat`;
    }

    function handleSseData(rawData, streamState, source) {
        const data = String(rawData || '').trim();
        if (!data) {
            return;
        }

        if (data === '[DONE]') {
            streamState.streamCompleted = true;
            return;
        }

        let payload;
        try {
            payload = JSON.parse(data);
        } catch {
            return;
        }

        const objectType = String(payload?.object || '');
        const delta = payload?.delta && typeof payload.delta === 'object' ? payload.delta : {};
        const content = delta.content;

        if (objectType === 'chat.completion.chunk' && typeof content === 'string') {
            streamState.assistantElement = appendAssistantChunk(streamState.assistantElement, content, source);
            return;
        }

        if (objectType === 'suggested_prompts' && Array.isArray(content)) {
            streamState.suggestedPrompts = content;
            return;
        }

        if (objectType === 'response_metadata' && content && typeof content === 'object') {
            streamState.responseMetadata = content;
            if (content.response_id) {
                previousResponseId = content.response_id;
            }
            return;
        }

        if (objectType !== 'tool_call') return;

        streamState.toolCalls.push(payload);

        if (delta.is_processing) {
            addRelatedNewsLoader(streamState.assistantElement);
            return;
        }

        removeRelatedNewsLoader(streamState.assistantElement);
        const toolData = delta.content && Array.isArray(delta.content.data)
            ? delta.content.data
            : null;
        if (!toolData) {
            return;
        }

        const mapped = mapToolArticles(toolData, source);
        const newArticles = mapped.filter(function (article) {
            return article.url && !streamState.seenArticleUrls.has(article.url);
        });

        newArticles.forEach(function (article) {
            streamState.seenArticleUrls.add(article.url);
        });
        streamState.toolCallArticles.push(...newArticles);

        if (!newArticles.length || !streamState.assistantElement) return;

        finalizeAssistantTextStreaming(streamState.assistantElement, source);
        if (!streamState.sharedArticlesGrid) {
            const articlesPayload = buildAssistantArticlesNode({ text: '', articles: newArticles });
            if (articlesPayload) {
                const textElement = streamState.assistantElement.querySelector('.chat-message-text');
                const newsLoader = streamState.assistantElement.querySelector('[data-role="related-news-loader"]');
                if (textElement) {
                    streamState.assistantElement.insertBefore(articlesPayload, textElement);
                } else if (newsLoader) {
                    streamState.assistantElement.insertBefore(articlesPayload, newsLoader);
                } else {
                    streamState.assistantElement.appendChild(articlesPayload);
                }

                streamState.sharedArticlesGrid = articlesPayload.querySelector('[data-role="articles-grid"]') || null;
                normalizeMessageLinks(streamState.assistantElement, source);
            }

            return;
        }

        newArticles.forEach(function (article) {
            const card = buildArticleCard(article, streamState.sharedArticlesGrid.children.length);
            if (card && streamState.sharedArticlesGrid) {
                streamState.sharedArticlesGrid.appendChild(card);
            }
        });
        normalizeMessageLinks(streamState.assistantElement, source);
    }

    async function runChatStream(message, loaderElement, source, requestUrl, promptContext) {
        const streamState = {
            assistantElement: loaderElement || null,
            suggestedPrompts: [],
            responseMetadata: null,
            toolCalls: [],
            toolCallArticles: [],
            seenArticleUrls: new Set(),
            sharedArticlesGrid: null,
            streamCompleted: false,
        };

        try {
            const response = await fetch(requestUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                    'Authorization': `Bearer ${source.auth.token}`,
                },
                body: JSON.stringify({
                    message: message,
                    identifier: source.auth.identifier,
                    customer: source.customer,
                    previous_response_id: previousResponseId,
                }),
            });

            if (!response.ok) {
                if (response.status === 400) {
                    const errorPayload = await response.json();
                    const serviceMessage = String(errorPayload.detail || '').trim();
                    const httpError = new Error(serviceMessage);
                    httpError.serviceMessage = serviceMessage;
                    throw httpError;
                }

                throw new Error(`HTTP ${response.status}: ${await response.text()}`);
            }

            if (!response.body) {
                throw new Error('ReadableStream not available');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done || streamState.streamCompleted) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                let newlineIndex = buffer.indexOf('\n');
                while (newlineIndex !== -1) {
                    const line = buffer.slice(0, newlineIndex).trim();
                    buffer = buffer.slice(newlineIndex + 1);

                    if (line.startsWith('data:')) {
                        handleSseData(line.slice(5), streamState, source);
                        if (streamState.streamCompleted) {
                            break;
                        }
                    }

                    newlineIndex = buffer.indexOf('\n');
                }
            }

            buffer += decoder.decode();

            if (!streamState.streamCompleted && buffer.trim().startsWith('data:')) {
                handleSseData(buffer.trim().slice(5), streamState, source);
            }

            finalizeAssistantTextStreaming(streamState.assistantElement, source);
            removeRelatedNewsLoader(streamState.assistantElement);

            renderPromptSuggestions(streamState.suggestedPrompts, source);

            const assistantText = String(streamState.assistantElement?.dataset?.markdownSource || '').trim();

            const result = {
                text: assistantText,
                meta: {
                    suggested_prompts: streamState.suggestedPrompts,
                    articles: streamState.toolCallArticles,

                },
                finalMeta: streamState.responseMetadata,
                responseId: streamState.responseMetadata?.response_id ?? null,
            };

            emitChatStreamCompleteEvent(message, result, source, promptContext);
            queueChatLog(message, result, streamState.toolCalls, source);

            return result;
        } catch (error) {
            removeRelatedNewsLoader(streamState.assistantElement);
            if (loaderElement && loaderElement.parentNode) {
                loaderElement.remove();
            }
            const networkError = isNetworkFetchError(error);
            if (networkError) {
                console.error('[cAIros] Chat API error:', error);
            } else {
                console.error('[cAIros] Chat API error:', error, error?.message);
            }

            const errorText = error?.serviceMessage || getUiText('error_generic', '');
            addMessage(errorText, 'assistant');

            queueChatLog(message, {
                detail: errorText,
                error: String(error?.message || error || ''),
            }, streamState.toolCalls, source);

            return null;
        } finally {
            if (loaderElement && loaderElement.parentNode && loaderElement.dataset.loading === 'thinking') {
                loaderElement.remove();
            }

            setStreamingState(false);
        }
    }

    async function chatStream(message, loaderElement, promptContext) {
        const source = globalThis.cairos.activeSource;
        if (!source) {
            console.error('[cAIros] No active source selected');
            setStreamingState(false);
            return null;
        }

        const apiUrl = String(source.api_url || '').trim();
        if (!apiUrl) {
            console.error('[cAIros] No API URL available');
            setStreamingState(false);
            return null;
        }

        const requestUrl = getChatRequestUrl(apiUrl);
        if (!requestUrl) {
            console.error('[cAIros] Unable to resolve chat request URL');
            setStreamingState(false);
            return null;
        }

        return runChatStream(message, loaderElement, source, requestUrl, promptContext);
    }

    function handleSuggestionClick(event, containerElement) {
        if (isStreaming) {
            return;
        }

        if (!(containerElement instanceof HTMLElement)) {
            return;
        }

        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const button = target.closest('.chat-suggestion-item');
        if (!button || !containerElement.contains(button) || button.hasAttribute('disabled')) {
            return;
        }

        const setId = button.dataset.suggestionSet;
        const selectedText = button.dataset.suggestionText || '';
        const selectedUrl = button.dataset.suggestionUrl || '';
        const selectedOrigin = button.dataset.suggestionOrigin || 'api';
        if (!setId || !selectedText) {
            return;
        }

        emitSuggestionClickTrackingEvent({
            text: selectedText,
            url: selectedUrl,
            action: selectedUrl ? 'open_url' : 'send_prompt',
            origin: selectedOrigin,
            isCta: selectedOrigin === 'additional',
        });

        if (selectedUrl) {
            globalThis.open(selectedUrl, '_blank', 'noopener');
            return;
        }

        const sameSetButtons = containerElement.querySelectorAll(`.chat-suggestion-item[data-suggestion-set="${setId}"]`);
        sameSetButtons.forEach(function (item) {
            if (item !== button) {
                item.hidden = true;
            }
        });

        if (selectedOrigin === 'initial') {
            clearInitialSuggestions();
        }

        button.disabled = true;
        button.classList.add('is-selected');

        const userMessage = handleUserPrompt(selectedText);
        if (!userMessage) {
            return;
        }

        const loaderElement = addThinkingLoader();
        setStreamingState(true);
        scrollToMessageStart(loaderElement, true);

        chatStream(selectedText, loaderElement, {
            type: 'suggestion',
            suggestionOrigin: selectedOrigin,
        }).catch(function (error) {
            console.error('[cAIros] Chat stream failed:', error);
        });
    }

    messages.addEventListener('click', function (event) {
        handleSuggestionClick(event, messages);
    });

    if (initialSuggestionsContainer instanceof HTMLElement) {
        initialSuggestionsContainer.addEventListener('click', function (event) {
            handleSuggestionClick(event, initialSuggestionsContainer);
        });
    }

    renderInitialSuggestions(globalThis.cairos.activeSource || null);

    form.addEventListener('submit', function (event) {
        event.preventDefault();
        if (isStreaming) {
            return;
        }

        const text = input.value.trim();
        const userMessage = handleUserPrompt(text);
        if (!userMessage) {
            return;
        }

        input.value = '';
        input.style.removeProperty('height');
        updateCurrentCharsLength();

        const loaderElement = addThinkingLoader();
        setStreamingState(true);
        scrollToMessageStart(loaderElement, true);

        chatStream(text, loaderElement, {
            type: 'manual',
        }).catch(function (error) {
            console.error('[cAIros] Chat stream failed:', error);
        });
    });

    setupCarouselFades(messages);

    input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
    });

    input.addEventListener('input', function () {
        updateCurrentCharsLength();

        if (!activated) {
            return;
        }

        input.style.height = 'auto';
        input.style.height = input.scrollHeight + 'px';
    });

    input.addEventListener('beforeinput', function (event) {
        if (shouldShakePromptChars(event)) {
            triggerPromptCharsLimitShake();
        }
    });

    const themeObserver = new MutationObserver(function (mutations) {
        const hasThemeChange = mutations.some(function (mutation) {
            return mutation.type === 'attributes' && mutation.attributeName === 'data-theme';
        });

        if (hasThemeChange) {
            refreshThemeSensitiveUi();
        }
    });

    themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
    });

    globalThis.cairos.simulateAssistantMessage = simulateAssistantMessage;
    globalThis.cairos.simulateAssistantHtmlMessage = simulateAssistantHtmlMessage;
    globalThis.cairos.simulateAssistantPayload = simulateAssistantPayload;
    globalThis.cairos.simulatePromptSuggestions = simulatePromptSuggestions;

    globalThis.dispatchEvent(new CustomEvent('cairos:chat-ready', {
        detail: {
            sourceSlug: String(globalThis.cairos?.activeSource?.slug || '').trim(),
            timestamp: new Date().toISOString(),
        },
    }));
})();
